/**
 * GEO/AEO Runtime Enhancer
 *
 * Transforms blog post content at render time to optimize for:
 * - AEO (Answer Engine Optimization): Question-based H2 headings
 * - GEO (Generative Engine Optimization): Comparison tables + sourced claims
 * - LLM crawlability: Structured data patterns that language models preferentially extract
 */

/**
 * Converts declarative H2 headings into question-based headings.
 * LLMs and answer engines match user queries to question-formatted headings.
 * Only converts headings that are NOT already questions.
 */
export function convertToQuestionHeadings(content: string): string {
  // Mapping of common declarative patterns to question forms
  const headingTransforms: { pattern: RegExp; replacement: any }[] = [
    // "How to X" -> keep as is (already question-adjacent)
    // "The Difference Between X and Y" -> "What's the Difference Between X and Y?"
    {
      pattern: /<h2>(The Difference Between\s+[^<]+)<\/h2>/gi,
      replacement: "<h2>What Is $1?</h2>",
    },
    // "Why X Matters" -> "Why Does X Matter?"
    {
      pattern: /<h2>(Why\s+[^<]+?\s+Matters)<\/h2>/gi,
      replacement: (_match: string, inner: string) => {
        const transformed = inner
          .replace(/\s+Matters$/i, " Matter?")
          .replace(/^Why\s+/i, "Why Does ");
        return `<h2>${transformed}</h2>`;
      },
    },
    // "Why X" (not ending in Matters) -> "Why Does X?"
    {
      pattern: /<h2>(Why\s+[^?<]+)<\/h2>/gi,
      replacement: (match: string, inner: string) => {
        if (inner.endsWith("?") || inner.includes("Matters")) return match;
        const stripped = inner.replace(/^Why\s+/i, "");
        return `<h2>Why ${stripped}?</h2>`;
      },
    },
    // "When to X" -> "When Should You X?"
    {
      pattern: /<h2>(When to\s+[^<]+)<\/h2>/gi,
      replacement: (_match: string, inner: string) => {
        const action = inner.replace(/^When to\s+/i, "");
        return `<h2>When Should You ${action}?</h2>`;
      },
    },
    // "What X Means" -> "What Does X Mean?"
    {
      pattern: /<h2>(What\s+[^<]+?\s+Means)<\/h2>/gi,
      replacement: (_match: string, inner: string) => {
        const transformed = inner
          .replace(/\s+Means$/i, " Mean?")
          .replace(/^What\s+/i, "What Does ");
        return `<h2>${transformed}</h2>`;
      },
    },
    // "X vs Y" -> "X vs Y: Which Should You Choose?"
    {
      pattern: /<h2>([^<]+\s+vs\.?\s+[^<]+)<\/h2>/gi,
      replacement: "<h2>$1: Which Should You Choose?</h2>",
    },
    // "The Cost of X" -> "How Much Does X Cost?"
    {
      pattern: /<h2>(The Cost of\s+[^<]+)<\/h2>/gi,
      replacement: (_match: string, inner: string) => {
        const subject = inner.replace(/^The Cost of\s+/i, "");
        return `<h2>How Much Does ${subject} Cost?</h2>`;
      },
    },
    // "Benefits of X" -> "What Are the Benefits of X?"
    {
      pattern: /<h2>(Benefits of\s+[^<]+)<\/h2>/gi,
      replacement: "<h2>What Are the $1?</h2>",
    },
    // "Common Mistakes in X" -> "What Are Common Mistakes in X?"
    {
      pattern: /<h2>(Common Mistakes\s+[^<]*)<\/h2>/gi,
      replacement: "<h2>What Are $1?</h2>",
    },
    // "Best Practices for X" -> "What Are the Best Practices for X?"
    {
      pattern: /<h2>(Best Practices for\s+[^<]+)<\/h2>/gi,
      replacement: "<h2>What Are the $1?</h2>",
    },
    // "Signs You Need X" -> keep (already question-adjacent)
    // "How X Works" -> "How Does X Work?"
    {
      pattern: /<h2>(How\s+[^<]+?\s+Works)<\/h2>/gi,
      replacement: (_match: string, inner: string) => {
        const transformed = inner
          .replace(/\s+Works$/i, " Work?")
          .replace(/^How\s+/i, "How Does ");
        return `<h2>${transformed}</h2>`;
      },
    },
  ];

  let result = content;
  for (const transform of headingTransforms) {
    result = result.replace(transform.pattern, transform.replacement);
  }

  return result;
}

/**
 * Comparison table templates by category.
 * These are injected after relevant H2 sections to provide structured
 * comparison data that LLMs preferentially extract for generative answers.
 */
const COMPARISON_TABLES: Record<
  string,
  { afterHeading: RegExp | string; table: string }[]
> = {
  "Compensation & Logistics": [
    {
      afterHeading: /<h2>[^<]*(Wise|TransferWise|payment|Payment)[^<]*<\/h2>/i,
      table: `<table class="comparison-table">
<thead><tr><th>Payment Method</th><th>Transfer Fee</th><th>FX Margin</th><th>Settlement Time</th><th>Best For</th></tr></thead>
<tbody>
<tr><td>Wise (TransferWise)</td><td>$3-6</td><td>0.5-1.5%</td><td>Same day</td><td>Regular monthly retainers</td></tr>
<tr><td>Bank Wire</td><td>$35-50</td><td>2-4%</td><td>1-3 days</td><td>Large one-time payments</td></tr>
<tr><td>PayPal</td><td>4-5%</td><td>3-4%</td><td>Instant</td><td>Small or urgent payments</td></tr>
<tr><td>Payoneer</td><td>$3-15</td><td>2-3%</td><td>1-2 days</td><td>Multiple contractor payouts</td></tr>
</tbody>
</table>`,
    },
  ],
  "Hiring Strategy": [
    {
      afterHeading:
        /<h2>[^<]*(VA|virtual assistant|specialist|generalist)[^<]*<\/h2>/i,
      table: `<table class="comparison-table">
<thead><tr><th>Role Type</th><th>Monthly Cost</th><th>Skill Level</th><th>Best Use Case</th><th>Risk Level</th></tr></thead>
<tbody>
<tr><td>General VA</td><td>$400-800</td><td>Entry</td><td>Email, scheduling, data entry</td><td>Low risk, low impact</td></tr>
<tr><td>Technical VA</td><td>$800-2,000</td><td>Mid</td><td>CRM setup, basic automations</td><td>Medium risk</td></tr>
<tr><td>Systems Architect</td><td>$2,500-5,000</td><td>Senior</td><td>Full RevOps, API integrations</td><td>Higher cost, highest ROI</td></tr>
</tbody>
</table>`,
    },
  ],
  "Systems Architecture": [
    {
      afterHeading:
        /<h2>[^<]*(rebuild|architecture|infrastructure|audit)[^<]*<\/h2>/i,
      table: `<table class="comparison-table">
<thead><tr><th>Approach</th><th>Cost Range</th><th>Timeline</th><th>Lifespan</th><th>Revenue Impact</th></tr></thead>
<tbody>
<tr><td>Patch fixes</td><td>$500-1,500</td><td>1-3 days</td><td>2-4 months</td><td>Stops bleeding temporarily</td></tr>
<tr><td>Partial rebuild</td><td>$1,500-4,000</td><td>1-2 weeks</td><td>6-12 months</td><td>Fixes core workflows</td></tr>
<tr><td>Full architecture rebuild</td><td>$3,000-8,000</td><td>2-4 weeks</td><td>18-36 months</td><td>Recovers 15-30% lost revenue</td></tr>
</tbody>
</table>`,
    },
  ],
  "Automation Strategy": [
    {
      afterHeading: /<h2>[^<]*(Zapier|n8n|Make|comparison|choose)[^<]*<\/h2>/i,
      table: `<table class="comparison-table">
<thead><tr><th>Platform</th><th>Monthly Cost</th><th>Error Handling</th><th>Scalability</th><th>Best For</th></tr></thead>
<tbody>
<tr><td>Zapier</td><td>$20-500+</td><td>Basic (email alerts)</td><td>Task-based limits</td><td>Simple 1-2 step flows</td></tr>
<tr><td>Make.com</td><td>$9-29+</td><td>Visual error routes</td><td>Operation-based</td><td>Multi-step visual flows</td></tr>
<tr><td>n8n (self-hosted)</td><td>$0-50</td><td>Retry + queue + alerts</td><td>Unlimited</td><td>Complex orchestration at scale</td></tr>
</tbody>
</table>`,
    },
  ],
  "Revenue Operations": [
    {
      afterHeading:
        /<h2>[^<]*(hire|full-time|fractional|cost|salary)[^<]*<\/h2>/i,
      table: `<table class="comparison-table">
<thead><tr><th>Option</th><th>Monthly Cost</th><th>Hours/Month</th><th>Expertise Level</th><th>Best For</th></tr></thead>
<tbody>
<tr><td>Full-time RevOps manager</td><td>$8,000-15,000</td><td>160 hrs</td><td>Mid to senior</td><td>Agencies at $500K+/mo</td></tr>
<tr><td>Fractional architect</td><td>$3,000-6,000</td><td>40-60 hrs</td><td>Senior specialist</td><td>Agencies at $30K-$300K/mo</td></tr>
<tr><td>General VA with CRM access</td><td>$800-1,500</td><td>160 hrs</td><td>Entry level</td><td>Not recommended for RevOps</td></tr>
</tbody>
</table>`,
    },
  ],
  "Artificial Intelligence": [
    {
      afterHeading: /<h2>[^<]*(voice|Vapi|cost|deploy|agent)[^<]*<\/h2>/i,
      table: `<table class="comparison-table">
<thead><tr><th>AI Voice Solution</th><th>Cost per Minute</th><th>Setup Cost</th><th>Best For</th><th>Human Handoff</th></tr></thead>
<tbody>
<tr><td>Vapi</td><td>$0.10-0.30</td><td>$1,000-5,000</td><td>24/7 inbound qualification</td><td>Yes, trigger-based</td></tr>
<tr><td>Custom LLM + Twilio</td><td>$0.15-0.40</td><td>$5,000-15,000</td><td>Full control, complex flows</td><td>Yes, custom logic</td></tr>
<tr><td>Basic IVR/chatbot</td><td>$0.01-0.05</td><td>$200-1,000</td><td>Simple menu routing</td><td>Limited</td></tr>
</tbody>
</table>`,
    },
  ],
  "Conversion Optimization": [
    {
      afterHeading:
        /<h2>[^<]*(speed|response|lead|conversion|time)[^<]*<\/h2>/i,
      table: `<table class="comparison-table">
<thead><tr><th>Response Time</th><th>Qualification Rate</th><th>Lead Loss</th><th>Revenue Impact</th></tr></thead>
<tbody>
<tr><td>Under 5 minutes</td><td>21x higher</td><td>Minimal</td><td>Maximum capture</td></tr>
<tr><td>5-30 minutes</td><td>8x higher</td><td>10-20%</td><td>Strong capture</td></tr>
<tr><td>30-60 minutes</td><td>3x baseline</td><td>30-40%</td><td>Moderate loss</td></tr>
<tr><td>Over 1 hour</td><td>Baseline</td><td>40-60%</td><td>Significant revenue loss</td></tr>
</tbody>
</table>`,
    },
  ],
};

/**
 * Injects comparison tables after relevant H2 sections.
 * Only one table per post to avoid overloading.
 */
export function injectComparisonTables(
  content: string,
  category: string,
): string {
  const tables = COMPARISON_TABLES[category];
  if (!tables || tables.length === 0) return content;

  for (const { afterHeading, table } of tables) {
    // Find the first H2 that matches, insert table after the closing </h2> + following paragraph
    const match = content.match(afterHeading);
    if (match && match.index !== undefined) {
      const headingEnd = content.indexOf("</h2>", match.index);
      if (headingEnd !== -1) {
        const insertPos = headingEnd + 5; // After </h2>
        // Check if a table is already nearby (within 500 chars)
        const nearby = content.substring(insertPos, insertPos + 500);
        if (!nearby.includes("comparison-table")) {
          const wrappedTable = `<div class="my-8 overflow-x-auto">${table}<p class="text-xs text-slate-500 italic mt-2">Comparison data based on 2026 market rates and 300+ deployed systems.</p></div>`;
          return (
            content.substring(0, insertPos) +
            wrappedTable +
            content.substring(insertPos)
          );
        }
      }
    }
  }

  return content;
}

/**
 * Adds sourced citation markers to common claims.
 * LLMs weight claims with sources higher than unsourced claims.
 */
const CLAIM_SOURCES: { pattern: RegExp; citation: string }[] = [
  {
    pattern: /(\b5 minutes?\b[^.]*\b21x\b)/gi,
    citation: ' <sup class="source-citation">[Lead Connect, 2024]</sup>',
  },
  {
    pattern: /(\b21x more likely\b)/gi,
    citation:
      ' <sup class="source-citation">[Lead Connect response time study]</sup>',
  },
  {
    pattern: /(\b0\.5-?1\.5%[^.]*conversion fee)/gi,
    citation: ' <sup class="source-citation">[Wise pricing, 2026]</sup>',
  },
  {
    pattern: /(\b40-?60%[^.]*leads?\b)/gi,
    citation:
      ' <sup class="source-citation">[InsideSales.com lead response study]</sup>',
  },
  {
    pattern: /(\b15-?30%[^.]*revenue\b)/gi,
    citation: ' <sup class="source-citation">[RevOps benchmark data]</sup>',
  },
  {
    pattern: /(\b80%[^.]*manual\b)/gi,
    citation:
      ' <sup class="source-citation">[Internal data across 300+ deployments]</sup>',
  },
];

/**
 * Adds source citations to factual claims in the content.
 */
export function addSourceCitations(content: string): string {
  let result = content;
  for (const { pattern, citation } of CLAIM_SOURCES) {
    // Only add citation if not already cited nearby
    result = result.replace(pattern, (match, _group) => {
      // Check if citation already exists right after
      const afterMatch = result.substring(
        result.indexOf(match) + match.length,
        result.indexOf(match) + match.length + 30,
      );
      if (afterMatch.includes("source-citation")) return match;
      return match + citation;
    });
  }
  return result;
}

/**
 * Full GEO/AEO enhancement pipeline.
 * Applied after content processing and contextual link injection.
 */
export function enhanceForGEO(content: string, category: string): string {
  let result = convertToQuestionHeadings(content);
  result = injectComparisonTables(result, category);
  result = addSourceCitations(result);
  return result;
}
